export type PaginationPosition = 'start' | 'center' | 'end' | 'any';

/** `null` — ellipsis slot */
export type PaginationItemValue = number | null;

export const determinePaginationPosition = (
    currentPage: number,
    totalPages: number,
    baseNumberPages: number
): PaginationPosition => {
    if (totalPages <= baseNumberPages) {
        return 'any';
    }

    const centerIndex = Math.ceil(baseNumberPages / 2);

    if (currentPage < centerIndex) {
        return 'start';
    }

    if (currentPage > totalPages - centerIndex + 1) {
        return 'end';
    }

    return 'center';
};

export const buildPaginationItems = (
    currentPage: number,
    totalPages: number,
    baseNumberPages: number
): PaginationItemValue[] => {
    const position = determinePaginationPosition(currentPage, totalPages, baseNumberPages);
    const centerIndex = Math.ceil(baseNumberPages / 2);
    const visiblePages =
        position === 'any' ? totalPages : position === 'center' ? baseNumberPages : baseNumberPages - 1;

    const getItem = (pageNumber: number): PaginationItemValue => {
        if (position === 'any') {
            return pageNumber;
        }

        if (pageNumber === 1) {
            return pageNumber;
        }

        if (pageNumber === visiblePages) {
            return totalPages;
        }

        if ((pageNumber === 2 && position !== 'start') || (pageNumber === visiblePages - 1 && position !== 'end')) {
            return null;
        }

        if (position === 'center') {
            return currentPage + pageNumber - centerIndex;
        }

        if (position === 'end') {
            return totalPages + pageNumber - visiblePages;
        }

        if (position === 'start') {
            return pageNumber;
        }

        return null;
    };

    return Array.from({ length: visiblePages }, (_, index) => getItem(index + 1));
};

const MOBILE_WINDOW_SIZE = 7;

/** Compact page window for mobile (≤767px) per design */
export const buildMobilePaginationItems = (currentPage: number, totalPages: number): PaginationItemValue[] => {
    if (totalPages <= MOBILE_WINDOW_SIZE) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const lastWindowStart = totalPages - 4;

    if (currentPage <= 4) {
        return [1, 2, 3, 4, 5, null, totalPages];
    }

    if (currentPage >= lastWindowStart) {
        return [1, null, totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, null, currentPage - 1, currentPage, currentPage + 1, null, totalPages];
};
